/**
 * ConfirmPH: Customer-facing review submission page (invite-only).
 *
 * Phase 32.1 (Feb 2026) replaced open public review submission with a
 * one-shot secure-token flow. A customer can only reach this page via
 * the email link sent automatically when their booking is marked
 * `completed` (or via a manual copy/resend from the business dashboard).
 *
 * Route: /review/:token
 * Public, no auth required.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Star, Loader2, CheckCircle2, AlertTriangle, Lock, ShieldCheck,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

/**
 * `preloaded`/`onSubmit`: optional sandboxed-demo hooks (used by the
 * homepage walkthrough), matching the same pattern already established on
 * RentalProfile/CustomerConfirmation. Skips the real
 * `/public/review-invites/:token` fetch and POST when provided. Real
 * behavior is unchanged when absent.
 */
export default function ReviewInvite({ preloaded = null, onSubmit = null } = {}) {
  const { token } = useParams();
  const [state, setState] = useState(
    preloaded ? { loading: false, invite: preloaded, error: null } : { loading: true, invite: null, error: null }
  );
  const [form, setForm] = useState({ rating: 0, title: "", comment: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (preloaded) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/review-invites/${token}`);
        if (!cancelled) setState({ loading: false, invite: r.data, error: null });
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail
                       || "We couldn't load this review link.";
        setState({
          loading: false, invite: null,
          error: { status, detail },
        });
      }
    })();
    return () => { cancelled = true; };
  }, [token, preloaded]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.rating < 1 || form.rating > 5) {
      toast.error("Please pick a star rating.");
      return;
    }
    if ((form.comment || "").trim().length < 10) {
      toast.error("Please write at least 10 characters in your review.");
      return;
    }
    setSubmitting(true);
    if (onSubmit) {
      onSubmit({ rating: form.rating, title: form.title.trim() || undefined, comment: form.comment.trim() });
      setDone(true);
      setSubmitting(false);
      return;
    }
    try {
      await axios.post(`${API}/public/review-invites/${token}/submit`, {
        rating: form.rating,
        title: form.title.trim() || undefined,
        comment: form.comment.trim(),
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit your review.");
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center text-slate-500 py-20"
             data-testid="review-invite-loading">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading review link…
        </div>
      </Shell>
    );
  }

  if (state.error) {
    return (
      <Shell>
        <Notice
          tone="rose"
          icon={<AlertTriangle className="w-5 h-5" />}
          testId="review-invite-error"
          title={
            state.error.status === 410
              ? "This review link is no longer valid"
              : "Review link not found"
          }
          body={
            state.error.status === 410
              ? "Review links can be used once and expire 30 days after they're sent. If you still want to share feedback, please contact the business directly."
              : state.error.detail
          }
        />
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <Notice
          tone="emerald"
          icon={<CheckCircle2 className="w-5 h-5" />}
          testId="review-invite-done"
          title="Thanks: your review was submitted"
          body={`Your review will appear publicly on ${state.invite.business_name}'s ConfirmPH profile once they publish it. Thanks for taking the time to share your honest experience.`}
        />
      </Shell>
    );
  }

  const inv = state.invite;

  return (
    <Shell>
      <div className="space-y-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-7"
             data-testid="review-invite-card">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">
            <ShieldCheck className="w-3.5 h-3.5" /> Booking-linked review
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold mt-2"
              data-testid="review-invite-heading">
            How was your rental with {inv.business_name}?
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            {inv.customer_name ? `Hi ${inv.customer_name.split(" ")[0]}, ` : ""}
            your review will appear publicly after the business publishes it.
            Please share your honest experience: there's no requirement to leave a positive review.
          </p>

          <form onSubmit={submit} className="mt-5 space-y-4" data-testid="review-invite-form">
            {/* Rating */}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                Your rating
              </label>
              <div className="mt-1.5 flex items-center gap-1" data-testid="review-rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    type="button"
                    key={n}
                    onClick={() => setForm({ ...form, rating: n })}
                    className="p-1"
                    aria-label={`${n} star${n === 1 ? "" : "s"}`}
                    data-testid={`star-${n}`}
                  >
                    <Star
                      className={
                        "w-7 h-7 transition " +
                        (n <= form.rating
                          ? "fill-amber-500 stroke-amber-500"
                          : "stroke-slate-300 hover:stroke-amber-400")
                      }
                    />
                  </button>
                ))}
                {form.rating > 0 && (
                  <span className="ml-2 text-sm text-slate-600">{form.rating}/5</span>
                )}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500"
                     htmlFor="rev-title">
                Title (optional)
              </label>
              <input
                id="rev-title"
                type="text"
                maxLength={120}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Smooth pickup, friendly staff"
                data-testid="review-title-input"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500"
                     htmlFor="rev-comment">
                Your review
              </label>
              <textarea
                id="rev-comment"
                rows={5}
                maxLength={2000}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                value={form.comment}
                onChange={(e) => setForm({ ...form, comment: e.target.value })}
                placeholder="What went well? Anything the next customer should know?"
                data-testid="review-comment-input"
              />
              <div className="mt-1 text-[11px] text-slate-400 text-right">
                {form.comment.length} / 2000
              </div>
            </div>

            <p className="text-[11px] leading-relaxed text-slate-500 flex items-start gap-1.5">
              <Lock className="w-3 h-3 mt-0.5 flex-shrink-0" />
              Your email is not shown publicly. Only your first name and last
              initial appear next to your review. By submitting, you confirm
              this is your honest experience.
            </p>

            <div className="flex items-center justify-end gap-2 pt-2">
              <Link to="/" className="text-sm text-slate-500 hover:text-slate-700">
                Cancel
              </Link>
              <button
                type="submit"
                disabled={submitting || form.rating < 1}
                className="px-4 py-2 rounded-lg bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-800 disabled:opacity-60 inline-flex items-center gap-2"
                data-testid="review-submit-btn"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Submit review
              </button>
            </div>
          </form>
        </div>

        <p className="text-[11px] text-center text-slate-400">
          Reviews on ConfirmPH are linked to real, completed bookings. We don't
          accept open reviews from random visitors. <Link to="/reviews-policy"
                                                          className="underline">
          Reviews policy</Link>.
        </p>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <Link to="/" className="font-display text-lg font-bold tracking-tight"
                data-testid="confirmph-logo">
            ConfirmPH
          </Link>
          <span className="text-[11px] text-slate-400 uppercase tracking-[0.18em]">
            Booking-linked review
          </span>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
}

function Notice({ tone, icon, title, body, testId }) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    rose:    "border-rose-200 bg-rose-50 text-rose-900",
  };
  return (
    <div className={"rounded-2xl border p-6 " + (tones[tone] || tones.emerald)}
         data-testid={testId}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5">{icon}</div>
        <div>
          <h2 className="font-display text-xl font-semibold">{title}</h2>
          <p className="mt-1 text-sm leading-relaxed">{body}</p>
          <Link to="/" className="mt-3 inline-block text-sm underline">
            Back to ConfirmPH
          </Link>
        </div>
      </div>
    </div>
  );
}
